import type { ControlDef, ControlValue, OrganDef } from "./types";

// F11: Organ-Name verlinkt in den „Verdauung Steuern"-CAS-Prototyp auf der
// Fokusgruppe-Subdomain, Deep-Link pro Organ per Hash. Schüler:innen können so
// aus dem Team-Spiel direkt zur biologischen Erklär-Simulation springen, ohne
// dass wir die Inhalte im Digest-Team-UI duplizieren.
const VS_DEEPLINK_BASE = "https://fokusgruppe.playful-mintful.ch/games/verdauung-steuern/";
// Map of our NodeId → hash in the „Verdauung Steuern" prototype. Entries
// without a hash fall back to the `#body`-anchor.
const VS_HASH_BY_ORGAN: Record<string, string> = {
  magen: "magen",
  leber: "leber",
  bauchspeicheldruese: "bauch",
  duenndarm: "duenn",
  // No existing VS anchors for mund/speiseroehre/gallenblase/zwoelffingerdarm/dickdarm/enddarm — fall back.
};

export interface RenderedPanel {
  root: HTMLElement;
  setValue(controlId: string, value: ControlValue): void;
  pulse(ok: boolean): void;
}

export type OnChange = (controlId: string, value: ControlValue) => void;

function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  className?: string,
  text?: string,
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  if (className) node.className = className;
  if (text !== undefined) node.textContent = text;
  return node;
}

export function renderPanel(
  organ: OrganDef,
  values: Record<string, ControlValue>,
  onChange: OnChange,
): RenderedPanel {
  const root = el("div", "organ-panel");

  const head = el("header", "organ-head");
  head.appendChild(el("span", "organ-emoji", organ.emoji));
  const title = el("h2");
  const link = document.createElement("a");
  link.className = "organ-deeplink";
  link.href = `${VS_DEEPLINK_BASE}#${VS_HASH_BY_ORGAN[organ.id] ?? "body"}`;
  link.target = "_blank";
  link.rel = "noopener noreferrer";
  link.title = `Biologische Erklärung zu ${organ.name} (Verdauung Steuern)`;
  link.textContent = organ.name;
  title.appendChild(link);
  head.appendChild(title);
  root.appendChild(head);

  // Stage: Organ-Bild mit nummerierten Callout-Markern.
  const stage = el("div", "organ-stage");
  const img = document.createElement("img");
  img.className = "organ-image";
  img.src = organ.image;
  img.alt = organ.name;
  stage.appendChild(img);

  const markerLayer = el("div", "markers");
  stage.appendChild(markerLayer);
  root.appendChild(stage);

  // Controls-Grid unterhalb, jede Karte mit passender Callout-Nummer.
  const controls = el("div", "controls");
  root.appendChild(controls);

  const setters: Record<string, (v: ControlValue) => void> = {};
  organ.controls.forEach((c, i) => {
    const num = i + 1;

    const marker = el("div", "marker", String(num));
    marker.style.left = `${c.anchor.x}%`;
    marker.style.top = `${c.anchor.y}%`;
    marker.title = c.label;
    markerLayer.appendChild(marker);

    const { node, set } = buildControl(c, values[c.id], (v) => {
      // Kurzer Flash auf dem zugehörigen Marker bei Interaktion.
      marker.classList.remove("marker-hit");
      void marker.offsetWidth;
      marker.classList.add("marker-hit");
      onChange(c.id, v);
    });
    node.prepend(el("span", "ctrl-num", String(num)));
    controls.appendChild(node);
    setters[c.id] = set;
  });

  return {
    root,
    setValue(id, v) {
      setters[id]?.(v);
    },
    pulse(ok) {
      root.classList.remove("pulse-ok", "pulse-err");
      void root.offsetWidth;
      root.classList.add(ok ? "pulse-ok" : "pulse-err");
    },
  };
}

function buildControl(
  c: ControlDef,
  initial: ControlValue,
  emit: (v: ControlValue) => void,
): { node: HTMLElement; set: (v: ControlValue) => void } {
  switch (c.kind) {
    case "button":
      return buildButton(c, emit);
    case "lever":
      return buildLever(c, Boolean(initial), emit);
    case "slider":
      return buildSlider(c, Number(initial), emit);
    case "dial":
      return buildDial(c, Number(initial), emit);
  }
}

function wrap(c: ControlDef): HTMLElement {
  const node = el("div", `ctrl ctrl-${c.kind}`);
  node.appendChild(el("div", "ctrl-label", c.label));
  return node;
}

function buildButton(c: ControlDef, emit: (v: ControlValue) => void) {
  const node = wrap(c);
  const btn = el("button", "big-btn", "PRESS");
  btn.type = "button";
  node.appendChild(btn);
  btn.addEventListener("click", () => {
    btn.classList.add("hit");
    setTimeout(() => btn.classList.remove("hit"), 180);
    emit(true);
  });
  return { node, set: (_v: ControlValue) => {} };
}

function buildLever(c: ControlDef, initial: boolean, emit: (v: ControlValue) => void) {
  const node = wrap(c);
  const lever = el("div", `lever ${initial ? "on" : "off"}`);
  lever.setAttribute("role", "switch");
  const knob = el("div", "lever-knob");
  const state = el("span", "lever-state", initial ? "AN" : "AUS");
  lever.appendChild(knob);
  lever.appendChild(state);
  node.appendChild(lever);
  let value = initial;
  const render = () => {
    lever.classList.toggle("on", value);
    lever.classList.toggle("off", !value);
    lever.setAttribute("aria-checked", String(value));
    state.textContent = value ? "AN" : "AUS";
  };
  render();
  lever.addEventListener("click", () => {
    value = !value;
    render();
    emit(value);
  });
  return {
    node,
    set: (v: ControlValue) => {
      value = Boolean(v);
      render();
    },
  };
}

function buildSlider(c: ControlDef, initial: number, emit: (v: ControlValue) => void) {
  const min = c.min ?? 0;
  const max = c.max ?? 10;
  const step = c.step ?? 1;
  const node = wrap(c);
  const w = el("div", "slider-wrap");
  const input = document.createElement("input");
  input.type = "range";
  input.min = String(min);
  input.max = String(max);
  input.step = String(step);
  input.value = String(initial);
  const out = document.createElement("output");
  out.className = "slider-out";
  out.textContent = `${initial}${c.unit ?? ""}`;
  w.appendChild(input);
  w.appendChild(out);
  node.appendChild(w);
  const render = (v: number) => {
    out.textContent = `${v}${c.unit ?? ""}`;
  };
  input.addEventListener("input", () => render(Number(input.value)));
  input.addEventListener("change", () => emit(Number(input.value)));
  return {
    node,
    set: (v: ControlValue) => {
      input.value = String(v);
      render(Number(v));
    },
  };
}

function buildDial(c: ControlDef, initial: number, emit: (v: ControlValue) => void) {
  const min = c.min ?? 0;
  const max = c.max ?? 9;
  const step = c.step ?? 1;
  const node = wrap(c);
  const w = el("div", "dial-wrap");
  const down = el("button", "dial-btn dial-down", "−");
  down.type = "button";
  const face = el("div", "dial-face");
  const val = el("span", "dial-val");
  face.appendChild(val);
  const up = el("button", "dial-btn dial-up", "+");
  up.type = "button";
  w.appendChild(down);
  w.appendChild(face);
  w.appendChild(up);
  node.appendChild(w);
  let v = initial;
  const render = () => {
    val.textContent = `${v}${c.unit ?? ""}`;
    const pct = (v - min) / (max - min);
    face.style.setProperty("--angle", `${-135 + pct * 270}deg`);
  };
  render();
  const bump = (dir: 1 | -1) => {
    v = Math.min(max, Math.max(min, v + dir * step));
    render();
    emit(v);
  };
  up.addEventListener("click", () => bump(1));
  down.addEventListener("click", () => bump(-1));
  return {
    node,
    set: (nv: ControlValue) => {
      v = Number(nv);
      render();
    },
  };
}
